import React, { useState } from 'react';
import {
  makeStyles,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Grid,
  Chip,
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import SendIcon from '@material-ui/icons/Send';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  title: {
    marginBottom: theme.spacing(3),
    color: theme.palette.primary.main,
    fontWeight: 'bold',
  },
  formControl: {
    marginBottom: theme.spacing(3),
    width: '100%',
  },
  submitButton: {
    marginTop: theme.spacing(2),
  },
  successIcon: {
    color: theme.palette.success.main,
    marginRight: theme.spacing(1),
  },
}));

const categories = [
  'Social Media',
  'Cloud Storage',
  'Email',
  'Banking',
  'Developer Tools',
  'Enterprise',
  'Gaming',
  'E-commerce',
  'Cryptocurrency',
  'Other',
];

export default function CompatibilityReport() {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    websiteName: '',
    websiteUrl: '',
    category: '',
    fido2Support: '',
    u2fSupport: '',
    nfcSupport: '',
    testResult: '',
    issues: '',
    setupSteps: '',
    deviceModel: '',
    browserVersion: '',
    notes: '',
  });

  const [submitted, setSubmitted] = useState(false);

  const handleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    // Validation
    if (!formData.websiteName || !formData.websiteUrl || !formData.testResult) {
      enqueueSnackbar(t('compatibility.error.required'), { variant: 'error' });
      return;
    }

    try {
      // TODO: 实际实现时，这里应该调用后端 API
      // const response = await fetch('/api/compatibility-reports', {
      //   method: 'POST',
      //   headers: { 'Content-Type': 'application/json' },
      //   body: JSON.stringify(formData),
      // });

      // 模拟提交
      console.log('Compatibility report submitted:', formData);

      setSubmitted(true);
      enqueueSnackbar(t('compatibility.success'), { variant: 'success' });

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({
          websiteName: '',
          websiteUrl: '',
          category: '',
          fido2Support: '',
          u2fSupport: '',
          nfcSupport: '',
          testResult: '',
          issues: '',
          setupSteps: '',
          deviceModel: '',
          browserVersion: '',
          notes: '',
        });
        setSubmitted(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting report:', error);
      enqueueSnackbar(t('compatibility.error.submit'), { variant: 'error' });
    }
  };

  if (submitted) {
    return (
      <Container className={classes.container} maxWidth="md">
        <Paper className={classes.paper}>
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <CheckCircleIcon style={{ fontSize: 80, color: '#4caf50' }} />
            <Typography variant="h4" style={{ marginTop: 20, marginBottom: 10 }}>
              {t('compatibility.thankYou')}
            </Typography>
            <Typography variant="body1" color="textSecondary">
              {t('compatibility.submitted')}
            </Typography>
          </div>
        </Paper>
      </Container>
    );
  }

  return (
    <Container className={classes.container} maxWidth="md">
      <Paper className={classes.paper}>
        <Typography variant="h4" className={classes.title}>
          📝 {t('compatibility.title')}
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          {t('compatibility.description')}
        </Typography>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Website Information */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                {t('compatibility.websiteInfo')}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label={t('compatibility.websiteName')}
                value={formData.websiteName}
                onChange={handleChange('websiteName')}
                placeholder="Google, GitHub, etc."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                required
                fullWidth
                label={t('compatibility.websiteUrl')}
                value={formData.websiteUrl}
                onChange={handleChange('websiteUrl')}
                placeholder="google.com"
              />
            </Grid>

            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>{t('compatibility.category')}</InputLabel>
                <Select
                  value={formData.category}
                  onChange={handleChange('category')}
                >
                  {categories.map((cat) => (
                    <MenuItem key={cat} value={cat}>
                      {cat}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Support Status */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom style={{ marginTop: 16 }}>
                {t('compatibility.supportStatus')}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl component="fieldset">
                <FormLabel component="legend">FIDO2 {t('compatibility.support')}</FormLabel>
                <RadioGroup
                  value={formData.fido2Support}
                  onChange={handleChange('fido2Support')}
                >
                  <FormControlLabel value="full" control={<Radio />} label={t('compatibility.full')} />
                  <FormControlLabel value="partial" control={<Radio />} label={t('compatibility.partial')} />
                  <FormControlLabel value="none" control={<Radio />} label={t('compatibility.none')} />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl component="fieldset">
                <FormLabel component="legend">U2F {t('compatibility.support')}</FormLabel>
                <RadioGroup
                  value={formData.u2fSupport}
                  onChange={handleChange('u2fSupport')}
                >
                  <FormControlLabel value="full" control={<Radio />} label={t('compatibility.full')} />
                  <FormControlLabel value="partial" control={<Radio />} label={t('compatibility.partial')} />
                  <FormControlLabel value="none" control={<Radio />} label={t('compatibility.none')} />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={4}>
              <FormControl component="fieldset">
                <FormLabel component="legend">NFC {t('compatibility.support')}</FormLabel>
                <RadioGroup
                  value={formData.nfcSupport}
                  onChange={handleChange('nfcSupport')}
                >
                  <FormControlLabel value="yes" control={<Radio />} label={t('compatibility.yes')} />
                  <FormControlLabel value="no" control={<Radio />} label={t('compatibility.no')} />
                  <FormControlLabel value="unknown" control={<Radio />} label={t('compatibility.unknown')} />
                </RadioGroup>
              </FormControl>
            </Grid>

            {/* Test Result */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom style={{ marginTop: 16 }}>
                {t('compatibility.testResult')}
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend">{t('compatibility.testResultLabel')}</FormLabel>
                <RadioGroup
                  value={formData.testResult}
                  onChange={handleChange('testResult')}
                  row
                >
                  <FormControlLabel
                    value="working"
                    control={<Radio />}
                    label={`✅ ${t('compatibility.working')}`}
                  />
                  <FormControlLabel
                    value="notWorking"
                    control={<Radio />}
                    label={`❌ ${t('compatibility.notWorking')}`}
                  />
                  <FormControlLabel
                    value="partial"
                    control={<Radio />}
                    label={`⚠️ ${t('compatibility.partialWork')}`}
                  />
                </RadioGroup>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('compatibility.issues')}
                value={formData.issues}
                onChange={handleChange('issues')}
                placeholder={t('compatibility.issuesPlaceholder')}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={4}
                label={t('compatibility.setupSteps')}
                value={formData.setupSteps}
                onChange={handleChange('setupSteps')}
                placeholder={t('compatibility.setupStepsPlaceholder')}
              />
            </Grid>

            {/* Device & Environment */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom style={{ marginTop: 16 }}>
                {t('compatibility.environment')}
              </Typography>
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('compatibility.deviceModel')}
                value={formData.deviceModel}
                onChange={handleChange('deviceModel')}
                placeholder="CanoKey, YubiKey, etc."
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label={t('compatibility.browserVersion')}
                value={formData.browserVersion}
                onChange={handleChange('browserVersion')}
                placeholder="Chrome 120, Firefox 121, etc."
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label={t('compatibility.notes')}
                value={formData.notes}
                onChange={handleChange('notes')}
                placeholder={t('compatibility.notesPlaceholder')}
              />
            </Grid>

            {/* Submit Button */}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                fullWidth
                className={classes.submitButton}
                startIcon={<SendIcon />}
              >
                {t('compatibility.submit')}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>

      {/* Info Section */}
      <Paper className={classes.paper}>
        <Typography variant="h6" gutterBottom>
          💡 {t('compatibility.tips')}
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          • {t('compatibility.tip1')}
        </Typography>
        <Typography variant="body2" color="textSecondary" paragraph>
          • {t('compatibility.tip2')}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          • {t('compatibility.tip3')}
        </Typography>
      </Paper>
    </Container>
  );
}
